import type { ReactNode } from "react";
import { Boxes, Calculator, Landmark, ShieldCheck, type LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { StatusBadge, type Tone } from "@/components/StatusBadge";
import type { IntegrityResponse, SourceBox } from "../../shared/api";
import { formatNumber } from "../../shared/versions";
import type { BoxName } from "../../shared/sources";
import { cn } from "@/lib/utils";

/**
 * Kutu başlığındaki rozet (spec §6 E2). Durum hesaplanır, ekran uydurmaz: API `status` vermedikçe (#16: R1/R1x/R6 bulguları)
 * rozet yoktur. Tek istisna dosya listesinden okunan "Boş". `status` gelince "✓ Güncel" ya da "⚠ <uyarı>" yazılır.
 */
export type BoxStatus = { status?: "current" | "warning"; warning?: string; warnings?: { rule: string }[]; missingDays?: number };
/** Rozet kısa kalır (ui.md §6.1): R1/R1x "Uyumsuzluk", R6 "N gün eksik kayıt" (spec §6 E2). Ayrıntı `boxDetail`'de, kutuda rozetin altında yazılır. */
export function boxBadge(box: SourceBox & BoxStatus): string | null {
  if (box.files.length === 0) return "Boş"; // dosya yokken "Güncel" olmaz (#159)
  if (box.status === "current") return "✓ Güncel";
  if (box.status === "warning") {
    if (!box.warnings) return `⚠ ${box.warning ?? "Uyarı"}`;
    const parts = [...(box.warnings.some((w) => w.rule !== "R6") ? ["Uyumsuzluk"] : []), ...(box.missingDays ? [`${formatNumber(box.missingDays)} gün eksik kayıt`] : [])];
    return `⚠ ${parts.join(" · ") || box.warning || "Uyarı"}`;
  }
  return null;
}
/** Rozet metni uyarının tamamını söylemiyorsa (ör. beyanname no) tam uyarı metni. */
export const boxDetail = (box: SourceBox & BoxStatus): string | null =>
  box.status === "warning" && box.warning && boxBadge(box) !== `⚠ ${box.warning}` ? box.warning : null;

/** Alt satır: "Toplam N kayıt · Yüklenen dosyaların bütünlüğü ✓". Bütünlük ayrıntısı #14/#15. */
export function footerText(total: number, integrity: IntegrityResponse | null): [label: string, result: string] {
  if (!integrity) return [`Toplam ${formatNumber(total)} kayıt`, ""]; // bütünlük sonucu (henüz) yok: ✓ yazılmaz
  const mark = integrity.failures.length === 0 ? "✓" : `✗ (${formatNumber(integrity.failures.length)} dosya)`;
  return [`Toplam ${formatNumber(total)} kayıt · Yüklenen dosyaların bütünlüğü `, mark];
}

/** Şerit kart (ui.md §12.11): sol "Toplam N kayıt" (`text-2xl`), sağ bütünlük rozeti. ✓/✗ metinde, rozet rengi ek. */
export function FooterLine({ total, integrity, empty = false, children, className }: { total: number; integrity: IntegrityResponse | null; empty?: boolean; children?: ReactNode; className?: string }) {
  if (empty) return <p className="min-h-5 text-sm text-muted-foreground">Henüz dosya yüklenmedi.</p>; // hiç dosya yok: "Toplam 0 kayıt · ✓" yazılmaz
  const [label, result] = footerText(total, integrity);
  const [count] = label.split(" · ");
  return (
    <Card data-slot="sources-strip" className={cn("px-4 py-3", className)}>
      {/* Spec cümlesi tek metin kalır: "Toplam N kayıt · Yüklenen dosyaların bütünlüğü ✓" */}
      <p><span className="text-2xl font-semibold tabular-nums">{count}</span>{integrity && <> <span className="text-muted-foreground">·</span> <StatusBadge tone={integrity.failures.length === 0 ? "success" : "critical"} className="align-middle text-sm">Yüklenen dosyaların bütünlüğü {result}</StatusBadge></>}</p>
      {children}
    </Card>
  );
}

/** Kutu rozetinin tonu (E2 kutu başlığı ve E1 Dört sistem aynı kuralı kullanır). */
export const boxTone = (box: SourceBox & BoxStatus): Tone =>
  box.files.length === 0 ? "info" : box.status === "warning" ? "warning" : box.status === "current" ? "success" : "info";

export const BOX_ICON: Record<BoxName, LucideIcon> = { customs: Landmark, erp: Boxes, accounting: Calculator, security: ShieldCheck };
